// Hidden, token-protected punch-fix tool. Reached by direct URL only
// (/commercial/attendance-punches) — no menu item. Lists present, job-linked
// attendance rows that are missing a clock-in or clock-out, and lets an admin
// fill/correct them. Sends wall-clock strings (no trailing Z) since the columns
// are tz-naive. Requires a valid auth token (localStorage 'token').
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Wrench, Lock, RefreshCw, Save, Check, AlertTriangle } from 'lucide-react';
import { utcToPKTInput, pktInputToUTC } from '../../helpers/time';

const API = process.env.REACT_APP_API_BASE_URL;

const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  border:  'var(--nd-w06)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx40)',
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .ap-wrap { font-family: 'DM Sans', sans-serif; color: var(--nd-text); }
  .ap-input {
    background: var(--nd-w05); border: 1px solid var(--nd-w12);
    border-radius: 8px; color: var(--nd-text); font-family: 'DM Sans', sans-serif;
    font-size: 13px; padding: 6px 10px; outline: none;
  }
  .ap-input:focus { border-color: rgba(96,165,250,0.5); }
  .ap-dt { font-family: 'DM Mono', monospace; font-size: 12px; }
  .ap-dt.ap-missing { border-color: rgba(245,158,11,0.6); }
  .ap-btn {
    background: var(--nd-w05); border: 1px solid var(--nd-w12);
    border-radius: 9px; color: var(--nd-tx70); font-weight: 700;
    font-size: 12px; padding: 8px 14px; cursor: pointer; display: inline-flex; align-items: center; gap: 7px;
  }
  .ap-btn:hover { background: var(--nd-w10); color: var(--nd-text-strong); }
  .ap-table { width: 100%; border-collapse: collapse; }
  .ap-table th { text-align: left; font-size: 9px; font-weight: 700; letter-spacing: 0.12em; color: var(--nd-tx35); text-transform: uppercase; padding: 10px 12px; border-bottom: 1px solid var(--nd-w08); white-space: nowrap; }
  .ap-table td { font-size: 13px; color: var(--nd-text); padding: 9px 12px; border-bottom: 1px solid var(--nd-w04); }
  .ap-save { background: rgba(96,165,250,0.15); border: 1px solid rgba(96,165,250,0.35); border-radius: 8px; color: #60A5FA; font-weight: 700; font-size: 12px; padding: 6px 12px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
  .ap-save:hover { background: rgba(96,165,250,0.25); }
  .ap-save:disabled { opacity: 0.4; cursor: default; }
  .ap-saved { color: #22D3A5; display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 700; }
`;

const todayStr = () => new Date().toISOString().split('T')[0];
const monthStartStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };

const AttendancePunches = () => {
  const token = (typeof window !== 'undefined') ? localStorage.getItem('token') : null;

  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});       // id -> { in, out }
  const [initial, setInitial] = useState({});    // id -> { in, out } (as loaded)
  const [savingId, setSavingId] = useState(null);
  const [savedIds, setSavedIds] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [from, setFrom] = useState(monthStartStr());
  const [to, setTo] = useState(todayStr());

  const authCfg = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchData = useCallback(async () => {
    if (!token) { setLoading(false); setError('auth'); return; }
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      const res = await axios.get(`${API}/ci-performance/incomplete-punches?${params.toString()}`, authCfg);
      const data = res.data?.data || [];
      setRows(data);
      const e = {}, init = {};
      for (const r of data) {
        const v = { in: utcToPKTInput(r.clock_in_at), out: utcToPKTInput(r.clock_out_at) };
        e[r.id] = { ...v };
        init[r.id] = { ...v };
      }
      setEdits(e);
      setInitial(init);
    } catch (err) {
      console.error('Incomplete-punches load error:', err);
      setError(err.response?.status === 401 ? 'auth' : 'Failed to load incomplete punches.');
    } finally {
      setLoading(false);
    }
  }, [from, to, token, authCfg]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setField = (id, field, value) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
    setSavedIds((prev) => ({ ...prev, [id]: false }));
  };

  const saveRow = async (id) => {
    const e = edits[id], init = initial[id] || {};
    if (!e) return;
    const body = {};
    if (e.in !== init.in) body.clock_in_at = pktInputToUTC(e.in);
    if (e.out !== init.out) body.clock_out_at = pktInputToUTC(e.out);
    if (Object.keys(body).length === 0) return;
    try {
      setSavingId(id);
      await axios.patch(`${API}/ci-performance/attendance/${id}/punch`, body, authCfg);
      setSavedIds((prev) => ({ ...prev, [id]: true }));
      // Drop the row if both punches are now present.
      if (e.in && e.out) {
        setTimeout(() => setRows((prev) => prev.filter((r) => r.id !== id)), 900);
      } else {
        setInitial((prev) => ({ ...prev, [id]: { ...e } }));
      }
    } catch (err) {
      console.error('Punch save error:', err);
      if (err.response?.status === 401) setError('auth');
      else alert(err.response?.data?.message || 'Failed to save punch.');
    } finally {
      setSavingId(null);
    }
  };

  if (error === 'auth') {
    return (
      <div className="ap-wrap" style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <style>{STYLES}</style>
        <div style={{ textAlign: 'center', color: T.muted }}>
          <Lock size={28} color="#F59E0B" />
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--nd-text-strong)', marginTop: 12 }}>Session required</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>This tool needs a valid login. Please sign in again, then reopen this page.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ap-wrap" style={{ background: T.bg, padding: '14px 20px', minHeight: '100vh' }}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: '20px 24px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Wrench size={20} color="#F59E0B" />
            </div>
            <div>
              <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Lock size={10} /> HIDDEN · TOKEN-PROTECTED
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.02em', lineHeight: 1 }}>Fix Attendance Punches</div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>Present records missing a clock-in or clock-out. Times are Pakistan time (PKT). Cycle credit stays 8h; this fixes the actual-hours record.</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 9, color: T.muted, letterSpacing: '0.12em' }}>FROM
              <input type="date" className="ap-input" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 9, color: T.muted, letterSpacing: '0.12em' }}>TO
              <input type="date" className="ap-input" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </label>
            <button className="ap-btn" onClick={fetchData} title="Refresh"><RefreshCw size={14} /> Refresh</button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', color: 'var(--nd-tx30)' }}>INCOMPLETE PUNCHES</div>
          <div style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Mono', monospace" }}>{loading ? '…' : `${rows.length} record${rows.length === 1 ? '' : 's'}`}</div>
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: T.muted, fontSize: 13 }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#F87171', fontSize: 13 }}>{error}</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#22D3A5', fontSize: 13 }}>
            <Check size={20} /> <div style={{ marginTop: 8 }}>No incomplete punches in this range.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="ap-table">
              <thead>
                <tr>
                  <th>Date</th><th>Client</th><th>Member</th>
                  <th>Clock-in (PKT)</th><th>Clock-out (PKT)</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const e = edits[r.id] || { in: '', out: '' };
                  return (
                    <tr key={r.id}>
                      <td style={{ fontFamily: "'DM Mono', monospace", color: 'var(--nd-tx70)' }}>{r.attendance_date}</td>
                      <td>{r.client_name || `#${r.client_id}`}</td>
                      <td style={{ fontWeight: 700 }}>{r.fo_name || `User #${r.fo_id}`}</td>
                      <td>
                        <input type="datetime-local" className={`ap-input ap-dt ${e.in ? '' : 'ap-missing'}`} value={e.in} onChange={(ev) => setField(r.id, 'in', ev.target.value)} />
                      </td>
                      <td>
                        <input type="datetime-local" className={`ap-input ap-dt ${e.out ? '' : 'ap-missing'}`} value={e.out} onChange={(ev) => setField(r.id, 'out', ev.target.value)} />
                        {!e.out && <AlertTriangle size={13} color="#F59E0B" style={{ marginLeft: 6, verticalAlign: 'middle' }} />}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {savedIds[r.id] ? (
                          <span className="ap-saved"><Check size={14} /> Saved</span>
                        ) : (
                          <button className="ap-save" disabled={savingId === r.id} onClick={() => saveRow(r.id)}>
                            <Save size={13} /> {savingId === r.id ? 'Saving…' : 'Save'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendancePunches;
